<script setup lang="ts">
import BaseTextField from '~/components/atoms/BaseTextField.vue'
import PersonIcon from '~/components/atoms/icons/PersonIcon.vue'
import TextButton from '~/components/atoms/TextButton.vue'
import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseIcon from '~/components/foundations/BaseIcon.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import TextBody from '~/components/foundations/TextBody.vue'
import BaseDialog from '~/components/molecules/BaseDialog.vue'
import type { Operator } from '~/entities/operator'

const {
  isActive,
  operators,
  hideCloseButton = false,
} = defineProps<{
  isActive: boolean
  operators: Operator[]
  hideCloseButton?: boolean
}>()

const emit = defineEmits<{
  'update:isActive': [value: boolean]
  select: [operator: Operator]
  add: []
}>()

const searchInput = ref<string>('')

const filteredOperators = computed(() => {
  if (!operators) return []
  if (!searchInput.value) return operators
  return operators.filter((operator) =>
    (operator.name + operator.name_pronunciation)
      .toLowerCase()
      .includes(searchInput.value.toLowerCase())
  )
})

const close = () => {
  emit('update:isActive', false)
}

const selectOperator = (operator: Operator) => {
  emit('select', operator)
  close()
}

const handleAddOperator = () => {
  emit('add')
}
</script>

<template>
  <BaseDialog
    :is-active="isActive"
    :hide-close-button="hideCloseButton"
    :title="$t('作業者を選択')"
    @close="close"
  >
    <template #content>
      <BaseStack class="operator-select-content">
        <BaseCluster justify="flex-end" align="center">
          <TextButton
            :text="$t('作業者の追加')"
            :color="'brand-strong'"
            :size="'large'"
            :icon="{
              type: 'add',
              position: 'prefix',
            }"
            @click="handleAddOperator"
          />
        </BaseCluster>

        <div class="search-input-block">
          <BaseTextField
            v-model="searchInput"
            :placeholder="$t('fieldを入力', { field: `${$t('名前')} / ${$t('読み方')}` })"
          >
            <template #icon>
              <BaseIcon type="search" :size="20" />
            </template>
          </BaseTextField>
        </div>

        <div class="operator-list-block">
          <ul v-if="filteredOperators.length > 0">
            <li v-for="operator in filteredOperators" :key="operator.id">
              <button class="operator-item" @click="selectOperator(operator)">
                <PersonIcon :size="24" />
                <TextBody :size="'large'" class="operator-name">{{ operator.name }}</TextBody>
              </button>
            </li>
          </ul>
        </div>
      </BaseStack>
    </template>
  </BaseDialog>
</template>

<style scoped lang="scss">
.operator-select-content {
  min-height: 300px;
  max-height: 500px;

  .search-input-block {
    padding-bottom: spacingToken.$spacing-xs;
    flex-shrink: 0;
  }

  .operator-list-block {
    flex: 1;
    overflow-y: auto;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      margin-bottom: spacingToken.$spacing-xs;
    }

    .operator-item {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: spacingToken.$spacing-xs spacingToken.$spacing-m;
      background-color: transparent;
      border: none;
      width: 100%;

      .operator-name {
        padding-left: spacingToken.$spacing-xs;
      }
    }
  }
}
</style>
